import { useCallback, useEffect, useState } from 'react';
import { useTour } from '@/hooks/onboarding/useTour';
import { TourRenderer } from './tour/TourRenderer';
import { useOnboarding } from '@/context/OnboardingContext';
import type { TourStep } from './tour/types';

function useAppTourSteps(): TourStep[] {
  return [
    {
      id: 'business-selector',
      target: 'business-selector',
      title: 'Switch businesses',
      description: 'Use this dropdown to switch between your businesses, create a new one, or view all businesses at once.',
      placement: 'bottom',
    },
    {
      id: 'dashboard-switcher',
      target: 'dashboard-switcher',
      title: 'Your dashboards',
      description: 'Switch between Customers for reviews, loyalty and marketing - and Team for scheduling, payroll and employees. Use the Portfolio button to the left of the business selector to see all businesses at a glance.',
      placement: 'bottom',
    },
    {
      id: 'sidebar',
      target: 'sidebar-nav',
      title: 'Sidebar navigation',
      description: 'Browse specific tools within each dashboard. The sidebar changes based on which dashboard you\'re on - Customers, Team, or Portfolio.',
      placement: 'right',
    },
    {
      id: 'business-settings',
      target: 'business-settings',
      title: 'Business settings',
      description: 'Configure your business details, opening hours, integrations, and feature visibility from here.',
      placement: 'bottom',
    },
    {
      id: 'ai-assistant',
      target: 'ai-assistant',
      title: 'AI assistant',
      description: 'Ask anything about your business, get help with tasks, or learn how features work. Your personal AI helper.',
      placement: 'bottom',
    },
    {
      id: 'notifications',
      target: 'notifications',
      title: 'Notifications',
      description: 'Stay on top of new reviews, employee requests, shift changes, and other updates. All in one place.',
      placement: 'bottom',
    },
  ];
}

export function AppTour() {
  const { isAppTourActive, completeAppTour, wizardSummary } = useOnboarding();
  const steps = useAppTourSteps();

  // Local flag so we can manage the tour lifecycle independently of context
  const [tourRequested, setTourRequested] = useState(false);

  const handleComplete = useCallback(() => {
    setTourRequested(false);
    completeAppTour();
  }, [completeAppTour]);

  const handleSkip = useCallback(() => {
    setTourRequested(false);
    completeAppTour();
  }, [completeAppTour]);

  const tour = useTour({
    steps,
    onComplete: handleComplete,
    onSkip: handleSkip,
  });

  // When context signals tour should start, set local flag
  useEffect(() => {
    if (isAppTourActive && !tourRequested && !tour.isActive) {
      setTourRequested(true);
    }
  }, [isAppTourActive, tourRequested, tour.isActive]);

  // When local flag is set, actually start the tour (separate effect to avoid batching issues)
  useEffect(() => {
    if (tourRequested && !tour.isActive) {
      tour.start();
    }
  }, [tourRequested, tour.isActive, tour.start]);

  return (
    <TourRenderer
      currentStep={tour.currentStep}
      currentIndex={tour.currentIndex}
      totalSteps={tour.totalSteps}
      isActive={tour.isActive}
      wizardSummary={wizardSummary}
      onNext={tour.next}
      onPrev={tour.prev}
      onSkip={tour.skip}
    />
  );
}
